import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { MessageSquare, TrendingUp } from "lucide-react";

// Buddy Insights — which tutorial step clients ask about most, so the
// onboarding logic can be sharpened where people actually get stuck.
export default function BuddyInsights() {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [days, setDays] = useState(30);

  const load = useCallback(async () => {
    try {
      setData((await api.get(`/buddy/analytics?days=${days}`)).data);
      setFailed(false);
    } catch {
      setData(null);
      setFailed(true);
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  if (failed) {
    return (
      <p className="font-mono text-xs mt-8" data-testid="buddy-insights-error">
        Buddy Insights is unavailable right now.
      </p>
    );
  }
  if (!data) return null;
  if (!data.total_questions) return null;
  const max = Math.max(1, ...data.by_step.map((s) => s.questions));

  return (
    <div className="hard-card p-5 mt-8" data-testid="buddy-insights">
      <div className="flex items-baseline justify-between gap-3 flex-wrap mb-4">
        <div>
          <div className="overline">§ BUDDY INSIGHTS</div>
          <div className="font-heading font-black text-2xl">Where clients get stuck</div>
        </div>
        <div className="flex items-center gap-2">
          {[7, 30, 90].map((d) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`buddy-insights-${d}d`}
              className={`border-2 border-ink px-3 py-1 font-mono text-xs ${days === d ? "bg-ink text-white" : "bg-white hover:bg-secondary"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 font-mono text-xs mb-5">
        <div className="border-2 border-ink p-3 flex items-center gap-2" data-testid="buddy-insights-total">
          <MessageSquare className="w-4 h-4" /> {data.total_questions} question(s)
        </div>
        <div className="border-2 border-ink p-3" data-testid="buddy-insights-proactive">
          {data.proactive_opens} proactive unblock(s)
        </div>
        <div className="border-2 border-ink p-3" data-testid="buddy-insights-scope">
          scope · {data.scope}
        </div>
      </div>

      <div className="space-y-2" data-testid="buddy-insights-steps">
        {data.by_step.map((s) => (
          <div key={s.key} data-testid={`buddy-insight-${s.key}`}>
            <div className="flex justify-between font-mono text-xs">
              <span>{s.label}</span>
              <span className="font-bold">{s.questions}</span>
            </div>
            <div className="border-2 border-ink h-4 mt-1 bg-white">
              <div className="h-full bg-ink" style={{ width: `${(s.questions / max) * 100}%` }} />
            </div>
          </div>
        ))}
      </div>

      {data.top_step_label && (
        <p className="font-mono text-xs mt-4 flex items-start gap-2" data-testid="buddy-insights-takeaway">
          <TrendingUp className="w-4 h-4 shrink-0" />
          Most-asked area: <strong>{data.top_step_label}</strong> — sharpen that step's instructions first.
        </p>
      )}
    </div>
  );
}
